import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { BankPicker } from '@/components/BankPicker'
import {
  createOrder,
  getAlertsUnreadCount,
  getDashboard,
  getExchanges,
  getExtractionQueueCount,
  getInventoryPositions,
  getOrders,
  getReconBoard,
  patchAccount,
  type BankLimitOut,
  type DashboardOut,
  type OrderOut,
  type SliceOut,
} from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  formatDay,
  formatDecimalInput,
  formatMoney,
  formatOrders,
  formatPct,
  formatQty,
  formatRate,
  multiply,
  parseAmount,
} from '@/lib/format'
import { useLocale } from '@/i18n/LocaleProvider'
import { t as translate } from '@/i18n'
import { LIMITS_SCROLL_EVENT, scrollToCabinAnchor } from '@/lib/scroll'

type Period = 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'range'

const PERIOD_KEY: Record<Period, string> = {
  today: 'period.today',
  yesterday: 'period.yesterday',
  '7d': 'period.7d',
  '30d': 'period.30d',
  all: 'period.all',
  range: 'period.range',
}

const SIMPLE_KEY = 'p2p.dashboard.simple'

function readSimple(): boolean {
  try {
    const raw = localStorage.getItem(SIMPLE_KEY)
    if (raw === null) return true
    return raw === '1'
  } catch {
    return true
  }
}

function ymdInTz(timezone: string, date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date)
}

export function Dashboard() {
  const { t } = useLocale()
  const { me } = useAuth()
  const location = useLocation()
  const timezone = me?.organization.timezone ?? 'UTC'
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<Period>('today')
  const [fromDate, setFromDate] = useState(() => ymdInTz(timezone))
  const [toDate, setToDate] = useState(() => ymdInTz(timezone))
  const [simple, setSimple] = useState(readSimple)

  useEffect(() => {
    try {
      localStorage.setItem(SIMPLE_KEY, simple ? '1' : '0')
    } catch {
      /* ignore quota */
    }
  }, [simple])

  const dash = useQuery({
    queryKey: ['dashboard', period, fromDate, toDate],
    queryFn: () =>
      period === 'range'
        ? getDashboard({ fromDate, toDate })
        : getDashboard({ period }),
  })
  const positions = useQuery({ queryKey: ['inventory', 'positions'], queryFn: getInventoryPositions })
  const queue = useQuery({ queryKey: ['extraction', 'queue-count'], queryFn: getExtractionQueueCount })
  const recon = useQuery({ queryKey: ['reconciliation'], queryFn: getReconBoard })
  const alerts = useQuery({ queryKey: ['alerts', 'unread'], queryFn: getAlertsUnreadCount })
  const activeOrders = useQuery({
    queryKey: ['orders', 'active-count'],
    queryFn: () => getOrders({ lifecycle: 'active', limit: '1' }),
  })

  const data = dash.data
  const asset = data?.asset_code || 'USDT'
  const pool = (positions.data ?? []).find((row) => row.asset_code === asset) ?? positions.data?.[0]
  const reconCount = (recon.data?.pending_review ?? 0) + (recon.data?.open_exceptions ?? 0)
  const shotCount = queue.data?.review ?? 0
  const alertCount = alerts.data?.count ?? 0
  const canOperate = ['owner', 'admin', 'operator'].includes(me?.organization.role ?? '')

  useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''))
    if (!id) return
    let tries = 0
    let timer = 0
    const tick = () => {
      if (scrollToCabinAnchor(id) || tries++ > 24) return
      timer = window.setTimeout(tick, 50)
    }
    timer = window.setTimeout(tick, 0)
    const onRepeat = () => {
      tries = 0
      tick()
    }
    window.addEventListener(LIMITS_SCROLL_EVENT, onRepeat)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener(LIMITS_SCROLL_EVENT, onRepeat)
    }
  }, [location.hash, data])

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t(PERIOD_KEY[period])}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {data
              ? t('dash.counts', { orders: formatOrders(data.orders), buy: data.buy_orders, sell: data.sell_orders })
              : t('dash.lead')}
          </p>
        </div>
        <div className="relative z-10 flex w-full flex-wrap items-center gap-2 md:w-auto">
          <button
            type="button"
            className="mma-refresh"
            disabled={dash.isFetching}
            onClick={() => {
              void dash.refetch()
              void positions.refetch()
              void queryClient.invalidateQueries({ queryKey: ['extraction'] })
              void queryClient.invalidateQueries({ queryKey: ['reconciliation'] })
              void queryClient.invalidateQueries({ queryKey: ['alerts'] })
              void queryClient.invalidateQueries({ queryKey: ['orders'] })
            }}
          >
            {dash.isFetching ? t('common.refreshing') : t('common.refresh')}
          </button>
          <button
            type="button"
            className={[
              'inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm',
              simple ? 'mma-period-on' : 'border border-surface-border text-slate-300',
            ].join(' ')}
            onClick={() => setSimple((value) => !value)}
            aria-pressed={simple}
          >
            <span className={['h-2 w-2 rounded-full', simple ? 'bg-profit' : 'bg-slate-500'].join(' ')} />
            {simple ? t('dash.simpleOn') : t('dash.simpleOff')}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <PeriodButtons period={period} onChange={setPeriod} />
        {period === 'range' && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="rounded-md border border-surface-border bg-surface px-2 py-1 text-slate-100"
            />
            <span className="text-slate-500">—</span>
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="rounded-md border border-surface-border bg-surface px-2 py-1 text-slate-100"
            />
          </div>
        )}
      </div>

      {dash.isLoading && (
        <p className="mt-8 text-sm text-slate-500" role="status">
          {t('dash.counting')}
        </p>
      )}
      {dash.isError && (
        <p className="mt-8 text-sm text-loss">
          {dash.error instanceof Error ? dash.error.message : t('common.error')}
        </p>
      )}

      {data && (
        <>
          <p className="mt-4 text-sm text-slate-400">
            {formatDay(data.from_at)} — {formatDay(data.to_at)}
          </p>
          {data.orders === 0 && (
            <div className="mt-4 rounded-lg border border-dashed border-surface-border bg-surface-raised px-4 py-3 text-sm text-slate-400">
              {t('dash.empty')}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Hero
              label={t('dash.profitPeriod')}
              value={formatMoney(data.profit_primary, data.primary_code)}
              current={Number(data.profit_primary)}
              previous={data.previous ? Number(data.previous.profit_primary) : null}
              previousLabel={data.previous ? formatMoney(data.previous.profit_primary, data.primary_code) : null}
              series={data.series.map((row) => Number(row.profit_fifo))}
              icon="profit"
              hideDelta={data.orders === 0}
            />
            <Hero
              label={t('dash.volume')}
              value={formatMoney(data.volume_primary, data.primary_code)}
              current={Number(data.volume_primary)}
              previous={data.previous ? Number(data.previous.volume_primary) : null}
              previousLabel={data.previous ? formatMoney(data.previous.volume_primary, data.primary_code) : null}
              series={data.series.map((row) => Number(row.volume_primary))}
              icon="volume"
              signed={false}
              hideDelta={data.orders === 0}
            />
            <Hero
              label={t('dash.spread')}
              value={data.spread_pct == null ? '—' : formatPct(data.spread_pct)}
              current={data.spread_pct == null ? Number.NaN : Number(data.spread_pct)}
              previous={data.spread_pct != null && data.previous?.spread_pct != null ? Number(data.previous.spread_pct) : null}
              previousLabel={
                data.spread_pct == null
                  ? t('dash.noBase')
                  : data.previous?.spread_pct != null
                    ? formatPct(data.previous.spread_pct)
                    : null
              }
              series={data.series.map((row) => Number(row.volume_primary))}
              icon="spread"
              hideDelta={data.orders === 0 || data.spread_pct == null}
            />
            <Hero
              label={t('dash.pool', { asset: pool?.asset_code ?? asset })}
              value={pool ? `${formatQty(pool.quantity_remaining)} ${pool.asset_code}` : '0'}
              current={pool ? Number(pool.quantity_remaining) : 0}
              previous={null}
              previousLabel={
                pool?.avg_unit_cost && pool.cost_currency
                  ? t('dash.cost', { value: formatMoney(pool.avg_unit_cost, pool.cost_currency) })
                  : t('dash.noLots')
              }
              series={data.series.map((row) => Number(row.orders))}
              icon="pool"
              signed={false}
              hideDelta
              extra={
                <Link to="/inventory" className="text-slate-300 hover:text-slate-100">
                  {t('dash.inventory')}
                </Link>
              }
            />
          </div>

          {!simple && (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat label={t('dash.bought', { asset })} value={formatQty(data.qty_bought)} />
                <Stat label={t('dash.sold', { asset })} value={formatQty(data.qty_sold)} />
                <Stat label={t('dash.vwapBuy')} value={formatRate(data.vwap_buy)} />
                <Stat label={t('dash.vwapSell')} value={formatRate(data.vwap_sell)} />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {t('dash.fees', { value: formatMoney(data.fees_primary, data.primary_code) })}
              </p>
            </>
          )}

          <BankLimits
            rows={data.limits}
            canEdit={canOperate}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
              void queryClient.invalidateQueries({ queryKey: ['accounts'] })
            }}
          />

          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
            {canOperate && (
              <div className="lg:order-2">
                <QuickTrade
                  data={data}
                  assetDefault={asset}
                  onSaved={() => {
                    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
                    void queryClient.invalidateQueries({ queryKey: ['inventory'] })
                    void queryClient.invalidateQueries({ queryKey: ['orders'] })
                    void queryClient.invalidateQueries({ queryKey: ['freshness'] })
                    void queryClient.invalidateQueries({ queryKey: ['reconciliation'] })
                  }}
                />
              </div>
            )}
            <div className={canOperate ? 'space-y-3 lg:order-1' : 'space-y-3'}>
              <OurRates data={data} asset={asset} />
              <div className="grid grid-cols-1 gap-2">
                <ActionLink
                  to="/orders?lifecycle=active"
                  label={t('dash.active')}
                  count={activeOrders.data?.total ?? 0}
                  warn
                />
                <ActionLink
                  to="/reconciliation"
                  label={t('dash.unmatched')}
                  count={(data.pending_recon + data.unmatched) || reconCount}
                  warn
                />
                <ActionLink to="/screenshots" label={t('dash.shots')} count={shotCount} warn={shotCount > 0} />
                <ActionLink to="/alerts" label={t('dash.alerts')} count={alertCount} warn={alertCount > 0} />
              </div>
            </div>
          </div>

          {!simple && (
            <div className="mt-8">
              <h2 className="text-sm font-medium text-slate-300">{t('dash.details')}</h2>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <ChartCard title={t('dash.volByDay', { ccy: data.primary_code })}>
                  <BarChart
                    points={data.series.map((row) => ({
                      label: row.day.slice(5),
                      value: Number(row.volume_primary),
                    }))}
                  />
                </ChartCard>
                <ChartCard title={t('dash.pnlByDay', { ccy: data.primary_code })}>
                  <BarChart
                    points={data.series.map((row) => ({
                      label: row.day.slice(5),
                      value: Number(row.profit_fifo),
                    }))}
                    positive
                  />
                </ChartCard>
              </div>
              <ChartCard title={t('dash.hour')} className="mt-4">
                <BarChart
                  points={data.by_hour.map((row) => ({ label: row.label.slice(0, 2), value: row.orders }))}
                />
              </ChartCard>
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <SliceTable title={t('dash.sliceExchange')} rows={data.by_exchange} currency={data.primary_code} />
                <SliceTable title={t('dash.sliceBank')} rows={data.by_bank} currency={data.primary_code} />
                <SliceTable title={t('dash.sliceAsset')} rows={data.by_asset} currency={data.primary_code} />
                <SliceTable title={t('dash.sliceMethod')} rows={data.by_payment_method} currency={data.primary_code} />
              </div>
              <div className="mt-4">
                <SliceTable title={t('dash.sliceCp')} rows={data.by_counterparty} currency={data.primary_code} />
              </div>
              <p className="mt-4 text-xs text-slate-500">
                {t('dash.reportsHint')}{' '}
                <Link to="/reports" className="text-slate-300 hover:text-slate-100">
                  {t('dash.reportsLink')}
                </Link>
                .
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function pairLabel(asset: string, fiat: string | null | undefined): string {
  if (!fiat) return asset
  return `${asset}/${fiat}`
}

function lastSideLine(
  kind: 'buy' | 'sell',
  price: string | null | undefined,
  asset: string,
  fiat: string | null | undefined,
  exchange: string | null | undefined,
  leading = false,
): string | null {
  if (price == null || price === '') return null
  const prefix =
    leading && kind === 'buy' ? translate('dash.lastBuy') : translate(kind === 'buy' ? 'common.buy.short' : 'common.sell.short')
  const venue = exchange ? ` · ${exchange}` : ''
  return `${prefix} ${formatRate(price)} ${pairLabel(asset, fiat)}${venue}`
}

function OurRates({ data, asset }: { data: DashboardOut; asset: string }) {
  const venues = data.by_exchange.filter((row) => row.last_buy_price || row.last_sell_price)
  const global = [
    lastSideLine('buy', data.last_buy_price, asset, data.last_buy_fiat, data.last_buy_exchange, true),
    lastSideLine('sell', data.last_sell_price, asset, data.last_sell_fiat, data.last_sell_exchange),
  ].filter(Boolean)
  const averages =
    data.vwap_buy || data.vwap_sell
      ? translate('rates.vwap', { buy: formatRate(data.vwap_buy), sell: formatRate(data.vwap_sell) })
      : null

  return (
    <div id="курси" className="scroll-mt-20 rounded-lg border border-surface-border bg-surface-raised px-4 py-3">
      <div className="text-[11px] text-slate-500">{translate('rates.title')}</div>
      {venues.length === 0 ? (
        <p className="mt-1 text-sm text-slate-400">{translate('rates.none')}</p>
      ) : venues.length === 1 ? (
        <>
          <p className="mt-1 text-sm text-slate-200">{global.join(' · ')}</p>
          {averages && <p className="mt-1 text-xs text-slate-500">{averages}</p>}
        </>
      ) : (
        <ul className="mt-1 space-y-1 text-sm text-slate-200">
          {venues.map((row) => {
            const buy = lastSideLine('buy', row.last_buy_price, asset, row.last_buy_fiat, null)
            const sell = lastSideLine('sell', row.last_sell_price, asset, row.last_sell_fiat, null)
            return (
              <li key={row.key}>
                {row.label}: {[buy, sell].filter(Boolean).join(' · ')}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function PeriodButtons({ period, onChange }: { period: Period; onChange: (value: Period) => void }) {
  const { t } = useLocale()
  return (
    <div className="flex flex-wrap gap-1 text-xs">
      {(
        [
          ['today', 'period.today'],
          ['yesterday', 'period.yesterday'],
          ['7d', 'period.7d'],
          ['30d', 'period.30d'],
          ['all', 'period.all'],
          ['range', 'period.range'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={[
            'rounded-md px-2.5 py-1 min-h-11 md:min-h-8',
            period === id ? 'mma-period-on' : 'text-slate-400 hover:text-gold-bright',
          ].join(' ')}
        >
          {t(label)}
        </button>
      ))}
    </div>
  )
}

function deltaPct(current: number, previous: number | null): string | null {
  if (previous == null || !Number.isFinite(previous) || previous === 0) return null
  if (!Number.isFinite(current)) return null
  const pct = ((current - previous) / Math.abs(previous)) * 100
  // L6: −100% лякає на порожньому/майже порожньому дні — лишаємо лише «було X».
  if (pct <= -99.95) return null
  return formatPct(pct)
}

function Hero({
  label,
  value,
  current,
  previous,
  previousLabel,
  series = [],
  icon,
  signed = true,
  hideDelta = false,
  extra,
}: {
  label: string
  value: string
  current: number
  previous: number | null
  previousLabel: string | null
  series?: number[]
  icon: 'profit' | 'volume' | 'spread' | 'pool'
  signed?: boolean
  hideDelta?: boolean
  extra?: ReactNode
}) {
  const change = hideDelta ? null : deltaPct(current, previous)
  const color = !signed
    ? 'text-slate-100'
    : current > 0
      ? 'text-profit'
      : current < 0
        ? 'text-loss'
        : 'text-slate-100'
  const iconClass =
    icon === 'profit'
      ? 'bg-profit/15 text-profit'
      : icon === 'spread'
        ? 'bg-pending/15 text-pending'
        : icon === 'pool'
          ? 'bg-[#22c55e]/15 text-[#22c55e]'
          : 'bg-slate-500/20 text-slate-300'
  const hint =
    previous != null && previousLabel
      ? `${translate('dash.was', { value: previousLabel })}${change ? ` · ${change}` : ''}`
      : previousLabel
        ? previousLabel
        : translate('dash.noCompare')
  return (
    <div className="rounded-lg border border-surface-border bg-surface-raised p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={['inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full', iconClass].join(' ')}>
            <KpiGlyph name={icon} />
          </span>
          <div className="text-[11px] leading-tight text-slate-500">{label}</div>
        </div>
        <Sparkline values={series} />
      </div>
      <div className={['num mt-1 text-xl sm:text-2xl', color].join(' ')}>{value}</div>
      <div className="mt-1 text-xs text-slate-500">
        {hint}
        {extra ? <> · {extra}</> : null}
      </div>
    </div>
  )
}

function Sparkline({ values }: { values: number[] }) {
  const points = values.filter((n) => Number.isFinite(n))
  if (points.length < 2) return null
  const min = Math.min(...points)
  const max = Math.max(...points)
  const span = max - min || 1
  const w = 64
  const h = 20
  const d = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * w
      const y = h - ((v - min) / span) * (h - 2) - 1
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0 text-slate-500" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function KpiGlyph({ name }: { name: 'profit' | 'volume' | 'spread' | 'pool' }) {
  const d =
    name === 'profit'
      ? 'M5 14l4-4 3 3 6-7'
      : name === 'volume'
        ? 'M6 16V9m6 7V6m6 10v-5'
        : name === 'spread'
          ? 'M5 12h14M12 5l7 7-7 7'
          : 'M12 5v14M7 9h10M7 15h10'
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
      <path d={d} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-surface-border bg-surface-raised p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="num mt-1 text-lg text-slate-100">{value}</div>
    </div>
  )
}

function BankLimits({
  rows,
  canEdit,
  onSaved,
}: {
  rows: BankLimitOut[]
  canEdit: boolean
  onSaved: () => void
}) {
  return (
    <div id="ліміти" className="mt-5 scroll-mt-4">
      <h2 className="text-sm font-medium text-slate-300">{translate('limits.title')}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-surface-border bg-surface-raised px-4 py-3 text-sm text-slate-400">
          {translate('limits.setOn')}{' '}
          <Link to="/accounts" className="text-slate-200 underline hover:text-white">
            {translate('limits.accounts')}
          </Link>
          .
        </p>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-500">
            {translate('limits.autoHint')}{' '}
            <Link to="/accounts" className="text-slate-300 hover:text-slate-100">
              {translate('limits.change')}
            </Link>
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {rows.map((row) => (
              <LimitCard key={row.account_id} row={row} canEdit={canEdit} onSaved={onSaved} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function LimitCard({
  row,
  canEdit,
  onSaved,
}: {
  row: BankLimitOut
  canEdit: boolean
  onSaved: () => void
}) {
  const currency = row.currency_code ?? ''
  const pct = Math.max(0, Number(row.pct) * 100)
  const over = Number(row.pct) >= 1
  const barColor = over ? 'bg-loss' : row.warn ? 'bg-pending' : 'bg-slate-400'
  const [draft, setDraft] = useState(row.override ? String(Number(row.used_final)) : '')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setDraft(row.override ? String(Number(row.used_final)) : '')
  }, [row.account_id, row.override, row.used_final])
  const save = useMutation({
    mutationFn: (value: string | null) =>
      patchAccount(row.account_id, { daily_used_override: value }),
    onSuccess: () => {
      setError(null)
      onSaved()
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : translate('quick.fail')),
  })

  return (
    <div className="rounded-lg border border-surface-border bg-surface-raised p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-slate-100">{row.name}</div>
        {row.warn && (
          <span className={['text-[11px]', over ? 'text-loss' : 'text-pending'].join(' ')}>
            {over ? translate('limits.exhausted') : translate('limits.warn')}
          </span>
        )}
      </div>
      <div className="num mt-1 text-lg text-slate-100">
        {formatMoney(row.used_final, currency)}
        <span className="text-sm text-slate-500">
          {' '}
          / {formatMoney(row.limit, currency)} · {formatPct(pct)}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div className={['h-full rounded-full', barColor].join(' ')} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
        <span>
          {translate('limits.auto', { value: formatMoney(row.used_auto, currency) })}
          {row.override ? translate('limits.manual') : ''}
        </span>
        <span>{translate('limits.left', { value: formatMoney(row.remaining, currency) })}</span>
      </div>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-[11px] text-slate-500">
            {translate('limits.fact')}
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="mt-1 block w-28 rounded-md border border-surface-border bg-surface px-2 py-1 text-sm text-slate-100 num"
              inputMode="decimal"
              placeholder={translate('limits.autoPh')}
            />
          </label>
          <button
            type="button"
            className="min-h-11 rounded-md border border-surface-border px-3 text-xs text-slate-200"
            onClick={() => {
              const parsed = parseAmount(draft)
              save.mutate(parsed === '' ? null : parsed)
            }}
            disabled={save.isPending}
          >
            {translate('limits.writeFact')}
          </button>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-loss">{error}</p>}
    </div>
  )
}

function QuickTrade({
  data,
  assetDefault,
  onSaved,
}: {
  data: DashboardOut
  assetDefault: string
  onSaved: () => void
}) {
  const last = useQuery({
    queryKey: ['orders', 'latest'],
    queryFn: () => getOrders({ limit: '1' }),
  })
  const exchanges = useQuery({ queryKey: ['exchanges'], queryFn: getExchanges })
  const latest: OrderOut | undefined = last.data?.items[0]
  const [side, setSide] = useState<'buy' | 'sell'>('buy')
  const [exchange, setExchange] = useState('')
  const [bankId, setBankId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [priceTouched, setPriceTouched] = useState(false)
  const [counterparty, setCounterparty] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const asset = latest?.asset_code || data.asset_code || assetDefault || 'USDT'
  const fiat = latest?.fiat_code || data.last_buy_fiat || data.last_sell_fiat || data.primary_code

  useEffect(() => {
    if (exchange) return
    const fromLast =
      (side === 'buy' ? data.last_buy_exchange : data.last_sell_exchange) || latest?.exchange_code
    setExchange(fromLast || 'binance')
  }, [data.last_buy_exchange, data.last_sell_exchange, exchange, latest?.exchange_code, side])

  useEffect(() => {
    if (bankId !== null || !latest?.bank_id) return
    setBankId(latest.bank_id)
  }, [bankId, latest?.bank_id])

  useEffect(() => {
    if (priceTouched) return
    const fromDash = side === 'buy' ? data.last_buy_price : data.last_sell_price
    const raw = fromDash || latest?.price
    if (raw) setPrice(formatDecimalInput(raw))
  }, [data.last_buy_price, data.last_sell_price, latest?.price, priceTouched, side])

  const total = useMemo(() => multiply(quantity, price), [price, quantity])

  const save = useMutation({
    mutationFn: async () => {
      const qty = parseAmount(quantity)
      const unit = parseAmount(price)
      if (!qty || !unit) {
        throw new Error(translate('quick.needQty'))
      }
      if (!exchange) {
        throw new Error(translate('quick.needEx'))
      }
      if (!bankId) {
        throw new Error(translate('quick.needBank'))
      }
      return createOrder({
        occurred_at: new Date().toISOString(),
        exchange_code: exchange,
        side,
        asset_code: asset,
        quantity: qty,
        price: unit,
        fiat_code: fiat,
        fiat_amount: total ? String(total) : null,
        bank_id: bankId,
        counterparty: counterparty.trim() || null,
        order_status: 'completed',
      })
    },
    onSuccess: (order) => {
      setError(null)
      setQuantity('')
      setPriceTouched(false)
      setOk(
        translate(order.side === 'buy' ? 'quick.savedBuy' : 'quick.savedSell', {
          qty: formatQty(order.quantity),
          asset: order.asset_code,
        }),
      )
      onSaved()
    },
    onError: (err: unknown) => {
      setOk(null)
      setError(err instanceof Error ? err.message : translate('quick.fail'))
    },
  })

  return (
    <form
      className="rounded-lg border border-surface-border bg-surface-raised p-4"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-slate-300">{translate('quick.title')}</h2>
        <Link to="/orders/new" className="text-xs text-slate-500 hover:text-slate-200">
          {translate('quick.full')}
        </Link>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        {translate('quick.hint', { pair: `${asset}/${fiat}` })}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="text-[11px] text-slate-500">
          {translate('common.side')}
          <select
            value={side}
            onChange={(event) => {
              setSide(event.target.value as 'buy' | 'sell')
              setPriceTouched(false)
            }}
            className={quickInput}
          >
            <option value="buy">{translate('common.buy')}</option>
            <option value="sell">{translate('common.sell')}</option>
          </select>
        </label>
        <label className="text-[11px] text-slate-500">
          {translate('common.exchange')}
          <select
            value={exchange}
            onChange={(event) => setExchange(event.target.value)}
            className={quickInput}
          >
            {(exchanges.data ?? []).map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <div className="col-span-2 text-[11px] text-slate-500">
          {translate('common.bank')}
          <div className="mt-1">
            <BankPicker fiat={fiat} value={bankId} onChange={(id) => setBankId(id)} />
          </div>
        </div>
        <label className="text-[11px] text-slate-500">
          {translate('quick.qty', { asset })}
          <input
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className={`${quickInput} num`}
            inputMode="decimal"
            placeholder="1000"
          />
        </label>
        <label className="text-[11px] text-slate-500">
          {translate('common.rate')}
          <input
            value={price}
            onChange={(event) => {
              setPriceTouched(true)
              setPrice(event.target.value)
            }}
            className={`${quickInput} num`}
            inputMode="decimal"
            placeholder="3.80"
          />
        </label>
        <label className="col-span-2 text-[11px] text-slate-500">
          {translate('common.counterparty')}
          <input
            value={counterparty}
            onChange={(event) => setCounterparty(event.target.value)}
            className={quickInput}
            placeholder={translate('common.optional')}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {translate('quick.sum', { fiat, value: total ? formatMoney(String(total), fiat) : '—' })}
      </p>
      {error && <p className="mt-2 text-sm text-loss">{error}</p>}
      {ok && <p className="mt-2 text-sm text-profit">{ok}</p>}
      <button
        type="submit"
        className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-md bg-[#22c55e] px-4 text-sm font-medium text-white disabled:opacity-60"
        disabled={save.isPending}
      >
        {translate('quick.save')}
      </button>
    </form>
  )
}

const quickInput = 'mt-1 w-full min-h-11 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm text-slate-100'

function ActionLink({
  to,
  label,
  count,
  warn,
}: {
  to: string
  label: string
  count: number
  warn?: boolean
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-raised px-4 py-3 text-sm hover:bg-slate-700/20"
    >
      <span className="text-slate-300">{label}</span>
      <span className={['num', warn && count > 0 ? 'text-pending' : 'text-slate-100'].join(' ')}>{count}</span>
    </Link>
  )
}

function ChartCard({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={['rounded-lg border border-surface-border bg-surface-raised p-4', className ?? ''].join(' ')}>
      <h2 className="text-sm font-medium text-slate-300">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function BarChart({ points, positive }: { points: { label: string; value: number }[]; positive?: boolean }) {
  if (points.length === 0) {
    return <p className="text-sm text-slate-500">{translate('dash.noPoints')}</p>
  }
  const max = Math.max(...points.map((item) => Math.abs(item.value)), 1)
  const width = 640
  const height = 160
  const gap = 4
  const bar = Math.max(3, (width - gap * points.length) / points.length)
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-40 w-full">
      {points.map((point, index) => {
        const h = (Math.abs(point.value) / max) * (height - 24)
        const x = index * (bar + gap)
        const y = height - 16 - h
        const color = positive && point.value < 0 ? '#f87171' : '#94a3b8'
        return (
          <g key={`${point.label}-${index}`}>
            <rect x={x} y={y} width={bar} height={h} rx={1} fill={color} />
            {points.length <= 24 && (
              <text x={x + bar / 2} y={height - 4} textAnchor="middle" className="fill-slate-500" fontSize="8">
                {point.label}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

function SliceTable({ title, rows, currency }: { title: string; rows: SliceOut[]; currency: string }) {
  return (
    <section className="rounded-lg border border-surface-border bg-surface-raised p-4">
      <h2 className="text-sm font-medium text-slate-300">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{translate('common.empty')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[20rem] text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr>
                <th className="py-1">{translate('dash.slice')}</th>
                <th className="py-1 text-right">{translate('dash.deals')}</th>
                <th className="py-1 text-right">{translate('dash.volume')}</th>
                <th className="py-1 text-right">{translate('nav.profit')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-t border-surface-border/60">
                  <td className="py-1.5">{row.label}</td>
                  <td className="num py-1.5 text-right">{row.orders}</td>
                  <td className="num py-1.5 text-right">{formatMoney(row.volume_primary, currency)}</td>
                  <td className="num py-1.5 text-right">{formatMoney(row.profit_fifo, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
